import { Check, MapPin, User, Zap } from "lucide-react";

import type { CarrierTrip } from "@/api/carrier";

/**
 * The handoff gate, rendered as three segments — Stops · Driver · Receivable.
 *
 * Ported from admin-web's `features/dispatch/readiness-chip.tsx`. A met segment
 * shows a green check; an unmet one is greyed with its own icon, so an
 * incomplete trip reads at a glance instead of requiring the card to be opened.
 * "Receivable" is met once the trip has BOTH stops and a driver — that is the
 * point at which the operator app can receive and work it.
 */

export interface TripReadiness {
  hasWaypoints: boolean;
  hasOperator: boolean;
  receivable: boolean;
}

/**
 * `waypointCount` is passed separately where waypoints load on their own (the
 * trips list, the dispatch rail); the trip's own array is used when it is there.
 */
export function tripReadiness(
  trip: Pick<CarrierTrip, "operatorId" | "waypoints">,
  waypointCount?: number
): TripReadiness {
  const stops = waypointCount ?? trip.waypoints?.length ?? 0;
  const hasWaypoints = stops > 0;
  const hasOperator = Boolean(trip.operatorId);
  return { hasWaypoints, hasOperator, receivable: hasWaypoints && hasOperator };
}

function Segment({
  met,
  icon,
  label,
}: {
  met: boolean;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <div
      className="flex items-center gap-1 rounded-md px-1.5 py-[3px]"
      style={{
        background: met ? "#dcfce7" : "#f4f4f5",
        color: met ? "#15803d" : "#a1a1aa",
      }}
      title={label}
    >
      {met ? <Check className="h-[11px] w-[11px]" strokeWidth={2.6} /> : icon}
      <span className="text-[10px] font-semibold uppercase tracking-wide">
        {label}
      </span>
    </div>
  );
}

export function ReadinessChip({
  trip,
  waypointCount,
}: {
  trip: Pick<CarrierTrip, "operatorId" | "waypoints">;
  waypointCount?: number;
}) {
  const readiness = tripReadiness(trip, waypointCount);
  return (
    <div className="flex items-center gap-1">
      <Segment
        met={readiness.hasWaypoints}
        icon={<MapPin className="h-[11px] w-[11px]" strokeWidth={2.6} />}
        label="Stops"
      />
      <Segment
        met={readiness.hasOperator}
        icon={<User className="h-[11px] w-[11px]" strokeWidth={2.6} />}
        label="Driver"
      />
      <Segment
        met={readiness.receivable}
        icon={<Zap className="h-[11px] w-[11px]" strokeWidth={2.6} />}
        label="Receivable"
      />
    </div>
  );
}
